import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useLazyGetDealRowQuery, useUpdateDealRowMutation } from '@/features/deals/dealsApi';
import { getDealClientId } from '@/features/deals/helper';
import {
	useLazyGetRelationshipCompanyQuery,
	useUpdateRelationshipCompanyMutation,
} from '@/features/relationshipCompanies/relationshipCompaniesApi';
import {
	useLazyGetClientRowQuery,
	useUpdateClientRowMutation,
} from '@/features/clients/clientsApi';
import {
	folderStructure,
	getFolderFromPipeline,
	getFolderStructure,
	replaceFolderName,
	formatDate,
	tryDecodeFilePath,
	getClientFolderStructure,
} from '@/utils/constants';
import { useAutomationContext } from '@/context/automationContext';
import { AUTOMATION_NAMES } from '@/utils/automationCatalog';
import { getClientFolderPipelineId } from '@/utils/additionalFolderAutomation';
import { useLazyListAdditionalFoldersQuery } from '@/features/additionalFolders/additionalFoldersApi';
import {
	useUpdateBrokerMutation,
	useGetBrokerGroupDetailsQuery,
	useLazyGetBrokerGroupDetailsQuery,
} from '@/features/user/userApi';
import { useMsGraph } from '@/hooks/useMsGraph'; // don't remove this import for some reason
import {
	useAddFileToSharepointMutation,
	useAddFolderToSharepointMutation,
	useLazyGetFoldersAndFilesQuery,
	useLazyGetSharepointDrivesQuery,
	useLazyGetSharepointListsQuery,
	useLazySearchFoldersAndFilesQuery,
	useLazySearchSharepointSitesQuery,
	useLazyGetDriveItemQuery,
	useLazyGetDriveItemFromUrlQuery,
	useBatchAddFoldersToSharepointMutation,
} from '@/features/msGraph/msGraphApi';
import { FieldValue } from 'firebase/firestore';
import { useGeneratePdfMutation } from '@/features/engagements/engagementsApi';
import { setSharepointError } from '@/features/table/attachmentsTableSlice';
import { getSharepointError } from '@/features/msGraph/helper';
import { showToast } from '@/features/toast/toastSlice';
// import { showToast } from '@/features/toast/toastSlice';
// import { buildSupportTicketPrefill } from '@/components/dialogs/IssueReportingDialog/supportTicketPrefill';

const flattenFolderTree = (tree, parentPath = '') => {
	const output = [];

	for (const [folderName, children] of Object.entries(tree ?? {})) {
		const path = parentPath ? `${parentPath}/${folderName}` : folderName;

		output.push(
			{
				path,
				name: folderName,
				parentPath: parentPath || null,
			},
			...flattenFolderTree(children, path),
		);
	}

	return output;
};

const checkIsValidSharepoint = (group) =>
	(group?.sharepointSite ?? group?.sharepointDomain) != null &&
	group?.clientSiteSuffix != null &&
	group?.clientFolder != null &&
	group?.clientSite != null;

export const useClientStorageHooks = (brokerGroupId) => {
	const dispatch = useDispatch();
	const { getAutomations } = useAutomationContext();
	const {
		data: brokerGroup,
		isLoading: isBrokerGroupLoading,
		isUninitialized: isBrokerGroupUninitialized,
	} = useGetBrokerGroupDetailsQuery({ docId: brokerGroupId }, { skip: !brokerGroupId });
	const [triggerGetBrokerGroupDetails] = useLazyGetBrokerGroupDetailsQuery();

	const [updateClientRow] = useUpdateClientRowMutation();
	const [updateBroker] = useUpdateBrokerMutation();
	const [updateDeal] = useUpdateDealRowMutation();
	const [updateRelationshipCompany] = useUpdateRelationshipCompanyMutation();
	const [triggerGetClientRow] = useLazyGetClientRowQuery();
	const [triggerGetDealRow] = useLazyGetDealRowQuery();
	const [triggerGetRelationshipCompany] = useLazyGetRelationshipCompanyQuery();

	const [getFoldersAndFiles] = useLazyGetFoldersAndFilesQuery();
	const [searchFoldersAndFiles] = useLazySearchFoldersAndFilesQuery();
	const [addFileToSharepoint] = useAddFileToSharepointMutation();
	const [addFolderToSharepoint] = useAddFolderToSharepointMutation();
	const [batchAddFoldersToSharepoint] = useBatchAddFoldersToSharepointMutation();
	const [getSharepointDrives] = useLazyGetSharepointDrivesQuery();
	const [getSharepointLists] = useLazyGetSharepointListsQuery();
	const [searchSharepointSites] = useLazySearchSharepointSitesQuery();
	const [getDriveItem] = useLazyGetDriveItemQuery();
	const [getDriveItemByUrl] = useLazyGetDriveItemFromUrlQuery();

	const [generatePdfMutation] = useGeneratePdfMutation();

	const { uploadFilesAndRetry } = useMsGraph();
	const [fetchAdditionalFolders] = useLazyListAdditionalFoldersQuery();

	const isValidSharepoint = checkIsValidSharepoint(brokerGroup);

	const getAdditionalFolders = useCallback(
		async (pipelineId) => {
			const automation = getAutomations(AUTOMATION_NAMES.additionalFolderCreation);
			if (!automation?.enabled || !brokerGroupId || !pipelineId) return [];

			try {
				const folders = await fetchAdditionalFolders({
					brokerGroupIds: [brokerGroupId],
					pipelineId,
				}).unwrap();
				return folders.map((folder) => folder.folderName);
			} catch (error) {
				console.error('Failed to load additional folders', error);
				dispatch(
					showToast({
						message: 'Failed to load additional folders',
						error: true,
						duration: 5000,
						autohide: true,
					}),
				);
				return [];
			}
		},
		[brokerGroupId, dispatch, fetchAdditionalFolders, getAutomations],
	);

	const reportSharepointError = useCallback(
		(error, objectId) => {
			const sharepointError = getSharepointError(error?.data ?? error);
			if (!sharepointError || !objectId) {
				return;
			}
			dispatch(setSharepointError({ ...sharepointError, objectId }));
			// Toast can fire before SharePoint finishes (e.g. slow drive fetch that later succeeds).
			// dispatch(
			// 	showToast({
			// 		id: 'sharepoint-documents-error',
			// 		message: sharepointError.error,
			// 		autohide: false,
			// 		action: {
			// 			label: 'Report Issue',
			// 			dialogName: 'issueReporting',
			// 			data: buildSupportTicketPrefill({
			// 				subject: sharepointError.error,
			// 				errorMessage: sharepointError.errorMessage,
			// 			}),
			// 		},
			// 	}),
			// );
		},
		[dispatch],
	);

	const clientSite = brokerGroup?.clientSite;
	const clientFolder = brokerGroup?.clientFolder;
	const clientSiteSuffix = brokerGroup?.clientSiteSuffix;
	const sharepointSiteId = brokerGroup?.sharepointSiteId;
	const userClientFolderDriveId = brokerGroup?.clientFolderDriveId;

	const getMatchingItem = useCallback(
		(list, name) =>
			list.find(
				(listItem) =>
					tryDecodeFilePath(listItem?.name ?? '', name) ||
					(listItem?.webUrl ?? '').toLowerCase().includes(name.toLowerCase()),
			),
		[],
	);

	const createFoldersFromNestedObject = useCallback(
		async (driveId, rootItemId, folderStructure) => {
			const flattenedFolders = flattenFolderTree(folderStructure);

			console.log('Flattened structure', flattenedFolders);
			const { createdFolders, failedFolders } = await batchAddFoldersToSharepoint({
				driveId,
				itemId: rootItemId,
				folders: flattenedFolders,
			}).unwrap();
			if (failedFolders?.length > 0) {
				console.warn(
					'[createFoldersFromNestedObject] Some folders failed to create',
					failedFolders,
				);
			}
			return { createdFolders, failedFolders };
		},
		[batchAddFoldersToSharepoint],
	);

	const getSite = useCallback(
		async (query) => {
			const siteId = await searchSharepointSites({
				searchText: query[query.length - 1],
			})
				.unwrap()
				.catch((e) => {
					console.log(
						`Error searching sharepoint sites by query ${query[query.length - 1]}`,
						e,
					);
				});
			console.log('SITE ID', siteId);
			return siteId;
		},
		[searchSharepointSites],
	);

	const getFolderDrive = useCallback(
		async (clientRow, driveId) => {
			const replacedCompanyName = replaceFolderName(clientRow?.name);

			const { folders: drives } = await searchFoldersAndFiles({
				itemId: 'root',
				driveId,
				searchText: replacedCompanyName,
				exact: true,
			}).unwrap();

			const drive = getMatchingItem(drives, replacedCompanyName);

			console.log(
				'CLIENT DRIVES',
				drives,
				'CLIENT DRIVE',
				drive,
				'FOLDER NAME',
				replacedCompanyName,
			);
			if (drive?.parentReference?.driveId && drive?.id) {
				const item = await getDriveItem({
					driveId: drive?.parentReference?.driveId,
					itemId: drive?.id,
				}).unwrap();
				console.log('[getFolderDrive] Here is the item', item);
				if (item?.folder) {
					const clientFolderDriveId = drive?.parentReference?.driveId;
					await updateClientRow({
						clientId: clientRow?.clientId,
						docId: clientRow?.docId,
						properties: {
							driveId: drive.id,
							...(clientFolderDriveId && { clientFolderDriveId }),
						},
					}).unwrap();
					return drive;
				}
			}
		},
		[searchFoldersAndFiles, getMatchingItem, getDriveItem, updateClientRow],
	);

	const searchForDealFolder = useCallback(
		async (name, driveId, clientFolderDriveId) => {
			const replacedName = replaceFolderName(name);
			const { folders: drives } = await searchFoldersAndFiles({
				itemId: driveId, //'root',
				driveId: clientFolderDriveId,
				searchText: replacedName,
			})
				.unwrap()
				.catch((e) => {
					console.log('Error searching for deal folder', e, { name: replacedName });
					return { folders: [] };
				});

			console.log(`DEAL FOLDER DRIVES from search: ${replacedName}`, drives);

			return getMatchingItem(drives, replacedName);
		},
		[searchFoldersAndFiles, getMatchingItem],
	);

	const getDealFolderDrive = useCallback(
		async (dealRow, driveId, clientFolderDriveId) => {
			let drive = await getDriveItemByUrl(dealRow?.dealFolderUrl)
				.unwrap()
				.catch((e) => {
					console.log('Error getting drive from url');
				});
			const name = replaceFolderName(dealRow?.dealName);
			if (!drive?.id) {
				drive = await searchForDealFolder(name, driveId, clientFolderDriveId);
			}

			console.log(`DEAL DRIVE for ${dealRow?.dealId} ${dealRow?.dealName}`, drive);
			if (drive?.parentReference?.driveId && drive?.id) {
				const clientFolderDriveId = drive?.parentReference?.driveId;
				await updateDeal({
					dealId: dealRow.dealId,
					docId: dealRow.id,
					properties: {
						driveId: drive.id,
						...(clientFolderDriveId && { clientFolderDriveId }),
					},
				}).unwrap();
				return drive;
			}
		},
		[getDriveItemByUrl, searchForDealFolder, updateDeal],
	);

	const getClientDriveId = useCallback(
		async (clientId) => {
			let driveId;
			let clientFolderDriveId;
			let clientRow;
			console.log(`getClientDriveId for client ${clientId}`);
			try {
				clientRow = await triggerGetClientRow(clientId).unwrap();

				console.log('CLIENT ROW DATA', {
					clientRow,
					driveId: clientRow?.driveId,
					clientFolderDriveId: clientRow?.clientFolderDriveId,
				});
				driveId = clientRow?.driveId;
				clientFolderDriveId = clientRow?.clientFolderDriveId;
				if (driveId && clientFolderDriveId) {
					const clearStaleClientDrive = async () => {
						driveId = null;
						clientFolderDriveId = null;
						await updateClientRow({
							clientId: clientRow?.clientId,
							docId: clientRow?.docId,
							properties: {
								driveId: FieldValue.delete(),
								clientFolderDriveId: FieldValue.delete(),
							},
						}).unwrap();
					};
					try {
						const item = await getDriveItem({
							driveId: clientFolderDriveId,
							itemId: driveId,
						}).unwrap();
						if (!item?.folder) {
							await clearStaleClientDrive();
						}
					} catch (e) {
						console.log('[getClientDriveId] Error getting drive item', e);
						reportSharepointError(e, clientRow?.clientId);
						return { clientRow, driveId, clientFolderDriveId };
					}
				}
			} catch (e) {
				console.log('Error getting client row', e);
			}

			const libraryDriveId =
				clientFolderDriveId ??
				clientRow?.brokerGroup?.clientFolderDriveId ??
				userClientFolderDriveId;
			if (!driveId || !clientFolderDriveId) {
				try {
					const drive = await getFolderDrive(clientRow, libraryDriveId);
					driveId = drive?.id;
					clientFolderDriveId = drive?.parentReference?.driveId;
					if (!driveId && libraryDriveId) {
						const item = await addFolderToSharepoint({
							driveId: libraryDriveId,
							itemId: 'root',
							folderName: replaceFolderName(clientRow?.name),
						}).unwrap();
						console.log('[getClientDriveId] ITEM', item, clientRow);
						clientFolderDriveId = libraryDriveId;
						driveId = item?.id;
						if (driveId) {
							await updateClientRow({
								clientId: clientRow?.clientId,
								docId: clientRow?.docId,
								properties: { driveId, clientFolderDriveId },
							}).unwrap();
						}
					}
				} catch (e) {
					console.log('Error getting client drive ID', e, clientRow);
					reportSharepointError(e, clientRow?.clientId);
				}
			}

			if (driveId && clientFolderDriveId) {
				try {
					const { folders } = await getFoldersAndFiles({
						itemId: driveId,
						driveId: clientFolderDriveId,
					}).unwrap();

					console.log('[getClientDriveId] FOLDERS', folders);

					const additionalClientFolders = await getAdditionalFolders(
						getClientFolderPipelineId(clientRow?.isProspect),
					);

					const foldersToCreate = getClientFolderStructure(
						clientRow?.name,
						new Date(),
						null,
						additionalClientFolders,
					);
					const existingFolderNames = new Set(folders.map((f) => f.name));
					const foldersToCreateKeys = Object.keys(foldersToCreate);
					const shouldCreate = foldersToCreateKeys.some(
						(folder) => !existingFolderNames.has(folder),
					);

					if (folders.length === 0 || shouldCreate) {
						console.log('FOLDERS TO CREATE', foldersToCreate);
						await createFoldersFromNestedObject(
							clientFolderDriveId,
							driveId,
							foldersToCreate,
						);
					}
				} catch (e) {
					console.log('Error getting client folders', e);
					reportSharepointError(e, clientRow?.clientId);
				}
			}

			return { clientRow, driveId, clientFolderDriveId };
		},
		[
			reportSharepointError,
			triggerGetClientRow,
			getDriveItem,
			updateClientRow,
			getFolderDrive,
			addFolderToSharepoint,
			getFoldersAndFiles,
			createFoldersFromNestedObject,
			userClientFolderDriveId,
			brokerGroupId,
			getAdditionalFolders,
		],
	);

	const createDealFolders = useCallback(
		async ({ dealId, dealName, clientId, clientName, pipelineId, date, yearFolder }) => {
			let effectiveBrokerGroup = brokerGroup;
			let effectiveIsValidSharepoint = isValidSharepoint;

			// brokerGroup query hasn't resolved yet — fetch it directly instead of
			// treating "not loaded" the same as "actually invalid"
			if (
				!isValidSharepoint &&
				brokerGroupId &&
				(isBrokerGroupLoading || isBrokerGroupUninitialized)
			) {
				effectiveBrokerGroup = await triggerGetBrokerGroupDetails({ docId: brokerGroupId })
					.unwrap()
					.catch((e) => {
						console.log('[createDealFolders] Error fetching broker group details', e);
						return brokerGroup;
					});
				effectiveIsValidSharepoint = checkIsValidSharepoint(effectiveBrokerGroup);
			}

			if (effectiveIsValidSharepoint) {
				const { driveId, clientRow, clientFolderDriveId } =
					await getClientDriveId(clientId);

				const additionalDealFolders = await getAdditionalFolders(pipelineId);
				const additionalClientFolders = await getAdditionalFolders(
					getClientFolderPipelineId(clientRow?.isProspect),
				);

				const pipelineFolder = getFolderFromPipeline(pipelineId);
				const trimmedDealName = replaceFolderName(dealName.trim());
				const replacedCompanyName = replaceFolderName(clientName);
				const { folders, dealFolder, clientSubFolders } = getFolderStructure({
					clientName: replacedCompanyName,
					dealName: trimmedDealName,
					// dealId,
					pipelineFolder,
					date,
					yearFolder,
					additionalDealFolders: additionalDealFolders,
					additionalClientFolders: additionalClientFolders,
				});

				console.log('FOLDERS TO CREATE', clientSubFolders, folders);
				const { createdFolders: folderItems, failedFolders } =
					await createFoldersFromNestedObject(
						clientFolderDriveId,
						driveId,
						clientSubFolders,
					);

				console.log('Folders created', folderItems, { failedFolders });
				const matchesPathSuffix = (path, suffix) =>
					path === suffix || path.endsWith(`/${suffix}`);

				const dealKey = Object.keys(folderItems).find((f) =>
					matchesPathSuffix(f, trimmedDealName),
				);
				const dealNotesKey = Object.keys(folderItems).find((f) =>
					matchesPathSuffix(f, `${trimmedDealName}/${folderStructure.notes}`),
				);
				const clientNotesKey = Object.keys(folderItems).find((f) =>
					matchesPathSuffix(
						f,
						tryDecodeFilePath(folderStructure.clientNotes.normalize()),
					),
				);

				const dealDriveId = folderItems[dealKey]?.id;
				const dealNotesDriveId = folderItems[dealNotesKey]?.id;
				const clientNotesDriveId = folderItems[clientNotesKey]?.id;

				console.log('FOLDER ITEMS', {
					folderItems,
					dealDriveId,
					dealNotesDriveId,
					clientNotesDriveId,
				});

				const dealFolderUrl = folderItems[dealKey]?.webUrl;

				console.log('dealId: ', dealId);

				if (!dealDriveId) {
					console.log(
						`[createDealFolders] Could not resolve the created deal folder for "${trimmedDealName}" among folderItems — aborting without writing deal folder metadata`,
						{ folderItems, failedFolders },
					);
					return {
						error: 'Could not resolve the created deal folder',
						dealFolder,
						folderItems,
						failedFolders,
					};
				}

				const doc = await updateDeal({
					dealId,
					properties: {
						...(dealFolderUrl && { dealFolderUrl }),
						dealFolderPath: dealFolder.url,
						yearRoot: dealFolder.yearRoot,
						yearFolder: dealFolder.yearFolder,
						pipelineFolder: dealFolder.pipelineFolder,
						clientName,
						driveId: dealDriveId,
						clientFolderDriveId,
						...(dealNotesDriveId && { notesDriveId: dealNotesDriveId }),
					},
				}).unwrap();

				if (clientNotesDriveId && !clientRow?.notesDriveId) {
					await updateClientRow({
						clientId,
						docId: clientRow?.docId,
						properties: { notesDriveId: clientNotesDriveId },
					})
						.unwrap()
						.catch((e) => console.log('Error updating client row', e));
				}

				return {
					success: 'Created client folders',
					dealFolder,
					dealFolderUrl,
					documentId: doc.id,
					driveId: dealDriveId,
					notesDriveId: dealNotesDriveId,
				};
			} else {
				console.log(
					'Could not create client folders, invalid Sharepoint site',
					effectiveBrokerGroup,
				);
				return {
					error: 'Could not create client folders, invalid Sharepoint site',
					brokerGroup: effectiveBrokerGroup,
				};
			}
		},
		[
			brokerGroup,
			brokerGroupId,
			isValidSharepoint,
			isBrokerGroupLoading,
			isBrokerGroupUninitialized,
			triggerGetBrokerGroupDetails,
			getClientDriveId,
			createFoldersFromNestedObject,
			updateDeal,
			updateClientRow,
			getAdditionalFolders,
		],
	);

	const getDealDriveId = useCallback(
		async (dealId) => {
			let driveId;
			let dealRow;
			let clientFolderDriveId;
			try {
				dealRow = await triggerGetDealRow(dealId).unwrap();
				console.log('DEAL ROW DATA', dealRow);
				driveId = dealRow?.driveId;
				clientFolderDriveId = dealRow?.clientFolderDriveId;
				if (driveId && clientFolderDriveId) {
					const clearStaleDealDrive = async () => {
						driveId = null;
						clientFolderDriveId = null;
						await updateDeal({
							dealId,
							properties: {
								driveId: FieldValue.delete(),
								clientFolderDriveId: FieldValue.delete(),
							},
						})
							.unwrap()
							.catch((e) => console.log('[getDealDriveId] error updating deal', e));
					};
					try {
						const item = await getDriveItem({
							driveId: clientFolderDriveId,
							itemId: driveId,
						}).unwrap();
						if (!item?.folder) {
							await clearStaleDealDrive();
						}
					} catch (e) {
						console.log('[getDealDriveId] Error getting drive item', e);
						reportSharepointError(e, dealRow?.dealId);
						return { dealRow, driveId, clientFolderDriveId };
					}
				}
			} catch (e) {
				console.log('Error getting deal row', e);
			}

			if (!driveId || !clientFolderDriveId) {
				const { clientRow, clientFolderDriveId: folderDriveId } = await getClientDriveId(
					getDealClientId(dealRow),
				);
				try {
					const drive = await getDealFolderDrive(
						dealRow,
						clientRow?.driveId,
						folderDriveId,
					);
					console.log('[getDealDriveId] DRIVE', drive);
					driveId = drive?.id;
					clientFolderDriveId = drive?.parentReference?.driveId;
				} catch (e) {
					console.log('Error getting deal drive ID', e);
				}

				clientFolderDriveId = folderDriveId;

				if (!driveId || !clientFolderDriveId) {
					const year = dealRow?.yearFolder;
					const years = (dealRow?.yearRoot ?? '').split('/');
					const yearFolder = year ?? years[years.length - 1] ?? '';
					const { driveId: dealDriveId } = await createDealFolders({
						dealId,
						dealName: dealRow?.dealName?.trim(),
						clientId: clientRow?.clientId,
						clientName: clientRow?.name,
						pipelineId: dealRow?.pipeline,
						yearFolder,
						// ...(dealRow?.yearRoot ? { yearFolder } : { date }),
					});
					driveId = dealDriveId;
				}
			}
			return { dealRow, driveId, clientFolderDriveId };
		},
		[
			reportSharepointError,
			triggerGetDealRow,
			getDriveItem,
			updateDeal,
			getClientDriveId,
			getDealFolderDrive,
			createDealFolders,
		],
	);

	const uploadDealFilesToSharepoint = useCallback(
		async ({ dealId, noteId, driveId, dealFolder, pipelineId, files }) => {
			const dealDriveId = driveId ?? (await getDealDriveId(dealId))?.driveId;
			console.log('[uploadDealFilesToSharepoint]', {
				dealId,
				noteId,
				driveId,
				dealDriveId,
				dealFolder,
				pipelineId,
				files,
			});
			await uploadFilesAndRetry({
				driveId: userClientFolderDriveId,
				itemId: dealDriveId,
				files,
			});
		},
		[userClientFolderDriveId, uploadFilesAndRetry, getDealDriveId],
	);

	const setUpDealFilesAndFolders = useCallback(
		async ({
			dealId,
			noteId,
			clientId,
			clientName,
			dealName,
			pipelineId,
			files,
			date,
			yearFolder,
			notes,
		}) => {
			console.log('[setUpDealFilesAndFolders] params ', {
				dealId,
				noteId,
				clientId,
				clientName,
				dealName,
				pipelineId,
				files,
				date,
				yearFolder,
				notes,
			});
			const { success, dealFolder, dealFolderUrl, documentId, driveId, notesDriveId } =
				await createDealFolders({
					dealId,
					dealName: dealName.trim(),
					clientId,
					clientName,
					pipelineId,
					date,
					yearFolder,
				});
			console.log('[setUpDealFilesAndFolders] createDealFolders response', {
				success,
				dealFolder,
				dealFolderUrl,
				documentId,
				driveId,
				notesDriveId,
			});
			if ((files ?? []).length > 0) {
				await uploadDealFilesToSharepoint({
					dealId,
					noteId,
					driveId,
					dealFolder,
					pipelineId,
					files,
				});
			}
			if ((notes ?? '').trim().length > 0) {
				try {
					const date = new Date();

					const fileName = `Created Note - ${formatDate(date, true).replace(':', '.')}`;
					const { blobUrl } = await generatePdfMutation({
						htmlContent: notes,
						fileName,
					}).unwrap();
					const blob = await fetch(blobUrl).then((r) => r.blob());
					const file = new File([blob], `${fileName}.pdf`, { type: 'application/pdf' });

					console.log('Creating note', { file, notesDriveId });
					try {
						let noteDriveId = notesDriveId;
						if (!noteDriveId) {
							const path = folderStructure.notes;
							console.log('Path for notes', path, driveId, userClientFolderDriveId);
							const { folders: drives } = await searchFoldersAndFiles({
								itemId: driveId,
								driveId: userClientFolderDriveId,
								searchText: decodeURI(folderStructure.notes.normalize()),
								exact: true,
							})
								.unwrap()
								.catch((e) => {
									console.log('Error searching for note folder', e);
									return { folders: [] };
								});

							let drive = drives.find((d) => tryDecodeFilePath(d.name, path));
							if (!drive) {
								console.log('Could not find drive through search');

								const { folders } = await getFoldersAndFiles({
									itemId: driveId,
									driveId: userClientFolderDriveId,
								})
									.unwrap()
									.catch((e) => {
										console.log('Error getting note folder', e);
										return { folders: [] };
									});

								console.log('FOLDERS', noteDriveId, folders);
								drive = folders.find((d) => tryDecodeFilePath(d.name, path));
							}
							noteDriveId = drive?.id;
							console.log('[searchClientGraphFolders] notes folders', {
								drives,
								drive,
								noteDriveId,
							});
						}

						if (noteDriveId) {
							console.log('[uploadClientSharepointFile]', { noteDriveId, file });
							await addFileToSharepoint({
								driveId: userClientFolderDriveId,
								itemId: noteDriveId,
								file,
							})
								.unwrap()
								.catch((e) => {
									console.error(
										'Error uploading generated pdf file via Graph API',
										file,
										e,
									);
									reportSharepointError(e, dealId);
								});
						}
					} catch (error) {
						console.log('🙅 ~ Error creating file', error);
					}
				} catch (error) {
					console.log('🚀 ~ Error creating note pdf', error);
				}
			}
			return { success, dealFolder, dealFolderUrl, documentId, driveId };
		},
		[
			createDealFolders,
			uploadDealFilesToSharepoint,
			generatePdfMutation,
			searchFoldersAndFiles,
			getFoldersAndFiles,
			addFileToSharepoint,
			userClientFolderDriveId,
			reportSharepointError,
		],
	);

	const createClientFolder = useCallback(
		async (dealId, contextValue) => {
			const { deal, client, yearFolder, date: dealDate } = contextValue;
			const date = dealDate ?? deal.createDate;

			console.log('Create client folder hook', contextValue);

			if (!client || !deal) {
				console.log('Could not create folder: could not locate client/deal', dealId);
				return;
			}

			await setUpDealFilesAndFolders({
				dealId,
				clientName: client?.name,
				clientId: client?.clientId,
				dealName: deal.dealName.trim(),
				pipelineId: deal.pipeline,
				date,
				yearFolder,
			});
		},
		[setUpDealFilesAndFolders],
	);

	const getSiteFiles = useCallback(
		async (siteId) => {
			console.log('GET SITE FILES', siteId);
			const drives = await getSharepointDrives({ siteId }).unwrap();
			const drive = getMatchingItem(drives, clientFolder);
			console.log(
				'DRIVES',
				drives,
				'DRIVE',
				drive,
				'userDetails?.clientFolder',
				clientFolder,
			);
			return drive?.id;
		},
		[getSharepointDrives, getMatchingItem, clientFolder],
	);

	const updateUserGraphProperties = useCallback(async () => {
		if (brokerGroup && (!sharepointSiteId || !userClientFolderDriveId)) {
			// const {
			// 	clientSite,
			// 	clientFolder,
			// 	clientSiteSuffix,
			// 	sharepointSiteId,
			// 	clientFolderDriveId,
			// } = userDetails ?? {};
			const siteUrl = `${clientSite}/${clientFolder}`;
			const response = await getDriveItemByUrl(siteUrl)
				.unwrap()
				.catch((e) => {
					console.log('Error getting drive item from url', { url: siteUrl }, e);
				});

			let folderDriveId = response?.parentReference?.driveId;
			let siteId = response?.parentReference?.siteId;
			if (!siteId && folderDriveId) {
				const { folders, files } = await getFoldersAndFiles({
					driveId: response?.parentReference?.driveId,
					itemId: 'root',
				}).unwrap();
				siteId =
					folders?.[0]?.parentReference?.siteId ?? files?.[0]?.parentReference?.siteId;
			} else if (!siteId && !folderDriveId) {
				const siteQuery = (clientSiteSuffix ?? '').split('/');
				if (siteQuery.length > 0) {
					siteId = await getSite(siteQuery);
				} else if (sharepointSiteId && !userClientFolderDriveId) {
					folderDriveId = await getSiteFiles(sharepointSiteId);
				}
			}
			if (siteId && folderDriveId) {
				await updateBroker({
					docId: brokerGroupId,
					properties: {
						sharepointSiteId: siteId,
						clientFolderDriveId: folderDriveId,
					},
				}).unwrap();
			}
		}
	}, [
		brokerGroup,
		brokerGroupId,
		clientSite,
		clientFolder,
		clientSiteSuffix,
		sharepointSiteId,
		userClientFolderDriveId,
		getDriveItemByUrl,
		getFoldersAndFiles,
		getSite,
		getSiteFiles,
		updateBroker,
	]);

	const getRelationshipCompanyDriveId = useCallback(
		async (relationshipCompanyId) => {
			let driveId;
			let clientFolderDriveId;
			let company;
			console.log(`getRelationshipCompanyDriveId for company ${relationshipCompanyId}`);
			try {
				company = await triggerGetRelationshipCompany(relationshipCompanyId).unwrap();
				driveId = company?.driveId;
				clientFolderDriveId = company?.clientFolderDriveId;
				if (driveId && clientFolderDriveId) {
					const clearStaleCompanyDrive = async () => {
						driveId = null;
						clientFolderDriveId = null;
						await updateRelationshipCompany({
							relationshipCompanyId: company?.id,
							properties: {
								driveId: null,
								clientFolderDriveId: null,
								notesDriveId: null,
							},
						})
							.unwrap()
							.catch((e) =>
								console.log(
									'[getRelationshipCompanyDriveId] error updating company',
									e,
								),
							);
					};
					try {
						const item = await getDriveItem({
							driveId: clientFolderDriveId,
							itemId: driveId,
						}).unwrap();
						if (!item?.folder) {
							await clearStaleCompanyDrive();
						}
					} catch (e) {
						console.log('[getRelationshipCompanyDriveId] Error getting drive item', e);
						reportSharepointError(e, company?.id);
						return { company, driveId, clientFolderDriveId };
					}
				}
			} catch (e) {
				console.log('Error getting relationship company', e);
			}

			const companyBrokerGroup = company?.brokerGroupId
				? await triggerGetBrokerGroupDetails({ docId: company.brokerGroupId })
						.unwrap()
						.catch((e) => {
							console.log(
								'[getRelationshipCompanyDriveId] Error fetching broker group',
								e,
							);
							return null;
						})
				: null;
			const libraryDriveId =
				clientFolderDriveId ??
				companyBrokerGroup?.clientFolderDriveId ??
				userClientFolderDriveId;

			if (!driveId || !clientFolderDriveId) {
				try {
					if (libraryDriveId) {
						const { folders: relationshipFolders = [] } = await searchFoldersAndFiles({
							itemId: 'root',
							driveId: libraryDriveId,
							searchText: folderStructure.relationships,
							exact: true,
						}).unwrap();
						let relationshipsRoot = getMatchingItem(
							relationshipFolders,
							folderStructure.relationships,
						);
						if (!relationshipsRoot) {
							relationshipsRoot = await addFolderToSharepoint({
								driveId: libraryDriveId,
								itemId: 'root',
								folderName: folderStructure.relationships,
							}).unwrap();
						}

						if (relationshipsRoot?.id) {
							const companyName = replaceFolderName(company?.name);
							const { folders: companyFolders = [] } = await searchFoldersAndFiles({
								itemId: relationshipsRoot.id,
								driveId: libraryDriveId,
								searchText: companyName,
								exact: true,
							}).unwrap();
							let companyFolder = getMatchingItem(companyFolders, companyName);
							if (!companyFolder) {
								companyFolder = await addFolderToSharepoint({
									driveId: libraryDriveId,
									itemId: relationshipsRoot.id,
									folderName: companyName,
								}).unwrap();
							}
							clientFolderDriveId = libraryDriveId;
							driveId = companyFolder?.id;
						}
					}
					if (driveId) {
						await updateRelationshipCompany({
							relationshipCompanyId: company?.id,
							properties: { driveId, clientFolderDriveId },
						}).unwrap();
					}
				} catch (e) {
					console.log('Error getting relationship company drive ID', e, company);
					reportSharepointError(e, company?.id);
				}
			}

			if (driveId && clientFolderDriveId) {
				try {
					const { folders } = await getFoldersAndFiles({
						itemId: driveId,
						driveId: clientFolderDriveId,
					}).unwrap();
					const existingNotesFolder = (folders ?? []).find(
						(folder) => folder.name === folderStructure.notes,
					);
					let notesDriveId = existingNotesFolder?.id ?? null;
					if (!notesDriveId) {
						const createdNotesFolder = await addFolderToSharepoint({
							driveId: clientFolderDriveId,
							itemId: driveId,
							folderName: folderStructure.notes,
						}).unwrap();
						notesDriveId = createdNotesFolder?.id ?? null;
					}
					if (notesDriveId && notesDriveId !== company?.notesDriveId) {
						await updateRelationshipCompany({
							relationshipCompanyId: company?.id,
							properties: { notesDriveId },
						}).unwrap();
					}
				} catch (e) {
					console.log('Error getting relationship company folders', e);
					reportSharepointError(e, company?.id);
				}
			}

			return { company, driveId, clientFolderDriveId };
		},
		[
			reportSharepointError,
			triggerGetRelationshipCompany,
			getDriveItem,
			updateRelationshipCompany,
			addFolderToSharepoint,
			searchFoldersAndFiles,
			getFoldersAndFiles,
			getMatchingItem,
			triggerGetBrokerGroupDetails,
			userClientFolderDriveId,
		],
	);

	const updateClientGraphProperties = useCallback(
		async ({ clientRow, dealRow, relationshipCompany }) => {
			if (brokerGroup && (!sharepointSiteId || !userClientFolderDriveId)) {
				updateUserGraphProperties();
			} else {
				console.log('[updateClientGraphProperties]', {
					'userDetails?.clientFolderDriveId': userClientFolderDriveId,
					'clientRow?.driveId': clientRow?.driveId,
					dealRow,
					'dealRow?.driveId': dealRow?.driveId,
					'dealRow?.clientFolderDriveId': dealRow?.clientFolderDriveId,
					'relationshipCompany?.driveId': relationshipCompany?.driveId,
				});
				if (userClientFolderDriveId) {
					if (clientRow && (!clientRow?.driveId || !clientRow?.clientFolderDriveId)) {
						return getClientDriveId(clientRow?.clientId);
					} else if (dealRow && (!dealRow.driveId || !dealRow.clientFolderDriveId)) {
						return getDealDriveId(dealRow?.dealId);
					} else if (
						relationshipCompany &&
						(!relationshipCompany.driveId || !relationshipCompany.clientFolderDriveId)
					) {
						return getRelationshipCompanyDriveId(relationshipCompany.id);
					}
				}
			}
		},
		[
			brokerGroup,
			sharepointSiteId,
			userClientFolderDriveId,
			updateUserGraphProperties,
			getClientDriveId,
			getDealDriveId,
			getRelationshipCompanyDriveId,
		],
	);

	return {
		createClientFolder,
		createDealFolders,
		uploadDealFilesToSharepoint,
		setUpDealFilesAndFolders,
		updateClientGraphProperties,
		getClientDriveId,
		getDealDriveId,
		getRelationshipCompanyDriveId,
		updateUserGraphProperties,
	};
};
